import { Component } from 'react';

import { dayjs } from '@personal-trainer-app/shared/src/utils/date';

import { reportBoundaryError } from '../reportBoundaryError';

import type { Props, State } from './PageErrorBoundary.types';
import type { ErrorInfo } from 'react';

import { isDebugMode } from '@/utils';

/**
 * Page Error Boundary Component
 *
 * This component catches JavaScript errors within individual pages/routes,
 * providing a more granular error handling approach than the global boundary.
 *
 * Features:
 * - Page-specific error handling
 * - Customizable fallback UI
 * - Option to retry or navigate back
 * - Maintains app navigation while showing error
 * - Less disruptive than global error boundary
 */
export class PageErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error
    this.logError(error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private logError = (error: Error, errorInfo: ErrorInfo) => {
    const errorDetails = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      pageName: this.props.pageName || 'Unknown Page',
      timestamp: dayjs().toISOString(),
      url: window.location.href,
    };

    if (isDebugMode) {
      console.group(
        `🚨 Page Error Boundary - Error in ${this.props.pageName || 'Page'}`
      );
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Page Error Details:', errorDetails);
      console.groupEnd();
    }

    reportBoundaryError(
      error,
      errorInfo,
      `page:${this.props.pageName || 'unknown'}`
    );
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  private renderErrorDetails = () => {
    if (!isDebugMode || !this.state.error) {
      return null;
    }

    return (
      <details className="mt-4 p-3 bg-error-wash border border-error-500/30 rounded">
        <summary className="text-body-sm font-medium text-error-400 cursor-pointer">
          🔧 Debug Info
        </summary>
        <div className="mt-2 space-y-2 text-label-xs text-error-300">
          <div>
            <strong>Error:</strong>
            <div className="font-mono bg-error-500/10 p-2 rounded mt-1">
              {this.state.error.message}
            </div>
          </div>
          {this.state.error.stack && (
            <div>
              <strong>Stack:</strong>
              <pre className="bg-error-500/10 p-2 rounded mt-1 overflow-auto max-h-32">
                {this.state.error.stack}
              </pre>
            </div>
          )}
        </div>
      </details>
    );
  };

  override render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default page error UI
      return (
        <div className="max-w-md mx-auto mt-8 p-6 bg-contrast-700 rounded-lg border border-error-500/30">
          {/* Error Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-error-wash rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-error-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>

          {/* Error Content */}
          <div className="text-center mb-6">
            <h2 className="font-display text-heading-sm font-semibold text-neutral-50 mb-2">
              {this.props.pageName
                ? `Error loading ${this.props.pageName}`
                : 'Page Error'}
            </h2>
            <p className="text-neutral-400 text-body-sm">
              We encountered an issue while loading this page. You can try again
              or go back to continue using the app.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            {this.props.showRetry !== false && (
              <button
                onClick={this.handleRetry}
                className="w-full py-2 px-4 bg-contrast-700 text-neutral-50 text-body-sm font-medium rounded hover:bg-lift-3 focus:outline-none focus-visible:ring-state-focus transition-colors"
              >
                Try Again
              </button>
            )}

            {this.props.showGoBack !== false && (
              <button
                onClick={this.handleGoBack}
                className="w-full py-2 px-4 bg-contrast-600 text-neutral-400 text-body-sm font-medium rounded hover:bg-lift-3 focus:outline-none focus-visible:ring-state-focus transition-colors"
              >
                Go Back
              </button>
            )}
          </div>

          {/* Debug Details */}
          {this.renderErrorDetails()}
        </div>
      );
    }

    return this.props.children;
  }
}

export default PageErrorBoundary;
